import { FormEvent, useEffect, useState } from "react";
import clsx from "clsx";
import { Navigate } from "react-router-dom";
import { useAuth } from "./useAuth";
import type { LoginResponse } from "../types/common";
import { useAuthStore } from "./authStore";
import { AUTH_API_PREFIX } from "./authApiPaths";
import { beginPkceLogin } from "./pkce";

const AUTH_ISSUER   = import.meta.env.VITE_AUTH_ISSUER    ?? "https://auth.mavr.finance";
const OAUTH_CLIENT  = import.meta.env.VITE_OAUTH_CLIENT_ID ?? "mavr-atlas-web";
const OIDC_REDIRECT = `${window.location.origin}/auth/oidc/callback`;

export function LoginPage() {
  const access = useAuthStore((s) => s.accessToken);
  const { login, loginTwoFactor, completeLogin } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [partialToken, setPartialToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [certAvailable, setCertAvailable] = useState(false);
  // SSO is the primary path; the password form stays available as a fallback
  // (default-open in dev, behind a toggle in production).
  const [showPassword, setShowPassword] = useState(!import.meta.env.PROD);

  useEffect(() => {
    fetch(`${AUTH_API_PREFIX}/cert-status`, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { cert_available: boolean } | null) => setCertAvailable(d?.cert_available ?? false))
      .catch(() => setCertAvailable(false));
  }, []);

  async function onSsoLogin() {
    setError(null);
    setBusy(true);
    try {
      await beginPkceLogin(AUTH_ISSUER, OAUTH_CLIENT, OIDC_REDIRECT);
    } catch (err) {
      setError(err instanceof Error ? err.message : "SSO redirect failed");
      setBusy(false);
    }
  }

  async function onCertLogin() {
    setError(null);
    setBusy(true);
    try {
      const res = await fetch(`${AUTH_API_PREFIX}/login/cert`, {
        method: "POST",
        credentials: "same-origin",
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Certificate login failed");
      }
      const data = (await res.json()) as LoginResponse;
      completeLogin(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Certificate login failed");
    } finally {
      setBusy(false);
    }
  }

  async function onPasswordSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res: LoginResponse = await login(username, password);
      if (res.requires_2fa) {
        setPartialToken(res.partial_token);
        return;
      }
      completeLogin(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setBusy(false);
    }
  }

  async function onTotpSubmit(e: FormEvent) {
    e.preventDefault();
    if (!partialToken) return;
    setError(null);
    setBusy(true);
    try {
      const res = await loginTwoFactor(partialToken, totp);
      completeLogin(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "2FA failed");
    } finally {
      setBusy(false);
    }
  }

  if (access) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-6">
      <div className="panel w-full max-w-md rounded border border-border bg-panel p-8 shadow-lg">
        <h1 className="mb-1 text-lg font-semibold text-neutral-100">
          {import.meta.env.VITE_APP_TITLE ?? "Atlas — Position Keeping"}
        </h1>
        <p className="mb-6 text-sm text-muted">Sign in with MAVR credentials</p>

        {!partialToken && (
          <div className="mb-5 space-y-2">
            {!import.meta.env.PROD && certAvailable && (
              <button
                type="button"
                disabled={busy}
                onClick={onCertLogin}
                className={clsx(
                  "w-full rounded border border-good/50 bg-good/10 py-2 text-sm font-medium text-good hover:bg-good/20 transition-colors",
                  busy && "opacity-60",
                )}
              >
                {busy ? "Authenticating…" : "🔒 Login with certificate"}
              </button>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={onSsoLogin}
              className={clsx(
                "w-full rounded border border-accent/50 bg-accent/10 py-2 text-sm font-medium text-accent hover:bg-accent/20 transition-colors",
                busy && "opacity-60",
              )}
            >
              {busy ? "Redirecting…" : "Sign in with MAVR SSO"}
            </button>
            {!showPassword && <div className="my-4 flex items-center gap-2">
              <div className="h-px flex-1 bg-border" />
              <button
                type="button"
                onClick={() => setShowPassword(true)}
                className="text-xs text-muted underline hover:text-neutral-300"
              >
                or sign in with password
              </button>
              <div className="h-px flex-1 bg-border" />
            </div>}
          </div>
        )}

        {showPassword && (!partialToken ? (
          <form onSubmit={onPasswordSubmit} className="space-y-4">
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wide text-muted">Username</label>
              <input
                className="w-full rounded border border-border bg-bg px-3 py-2 text-sm outline-none focus:border-accent"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wide text-muted">Password</label>
              <input
                type="password"
                className="w-full rounded border border-border bg-bg px-3 py-2 text-sm outline-none focus:border-accent"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            {error && <p className="text-sm text-bad">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className={clsx("btn w-full py-2 text-sm font-medium", busy && "opacity-60")}
            >
              {busy ? "Signing in…" : "Continue"}
            </button>
          </form>
        ) : (
          <form onSubmit={onTotpSubmit} className="space-y-4">
            <p className="text-sm text-muted">Enter the 6-digit code from your authenticator.</p>
            <input
              className="w-full rounded border border-border bg-bg px-3 py-2 text-sm tracking-widest outline-none focus:border-accent"
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
            />
            {error && <p className="text-sm text-bad">{error}</p>}
            <div className="flex gap-2">
              <button type="button" className="btn flex-1 py-2 text-sm" onClick={() => setPartialToken(null)}>
                Back
              </button>
              <button type="submit" disabled={busy || totp.length < 6} className="btn flex-1 py-2 text-sm font-medium">
                {busy ? "…" : "Verify"}
              </button>
            </div>
          </form>
        ))}
      </div>
    </div>
  );
}
